import * as RadixAvatar from "@radix-ui/react-avatar";
import type * as React from "react";
import { getInitialsColor } from "senf-shared";
import { type VariantProps, tv } from "tailwind-variants";
import { cn } from "../../../util/classNameConstructor";
import Icon from "../icon/Icon";

const AvatarStyles = tv({
  slots: {
    img: "h-full w-full object-cover object-top duration-100 group-hover:brightness-90",
    fallback:
      "flex flex-col justify-center rounded-full text-center font-bold text-white duration-100 group-hover:brightness-90",
  },
  variants: {
    borderRadius: {
      full: {
        img: "rounded-full",
      },
      large: {
        img: "rounded-xl",
      },
    },
    size: {
      xs: {
        img: "h-6 w-6",
        fallback: " h-6 w-6 text-xs",
      },
      sm: {
        img: "h-9 w-9",
        fallback: "h-9 w-9 text-sm",
      },
      md: {
        img: "h-11 w-11",
        fallback: "h-11 w-11 text-xl",
      },
      lg: {
        img: "h-[60px] w-[60px]",
        fallback: " h-[60px] w-[60px] text-xl",
      },
    },
  },
});

type AvatarProps = VariantProps<typeof AvatarStyles> & {
  src?: string;
  notLoggedIn?: boolean;
  elements?: React.ReactNode;
  props?: any;
  alt?: string;
  borderRadius?: "full" | "large";
  size?: "xs" | "sm" | "md" | "lg";
  onClick?: () => void;
};

const Avatar = ({
  id,
  notLoggedIn,
  src,
  onClick,
  elements,
  dataCy,
  borderRadius = "full",
  alt = "Username",
  size = "md",
  ...props
}: AvatarProps) => {
  const { img, fallback } = AvatarStyles({ borderRadius, size });

  const getInitials = (alt) => {
    if (!alt) return;
    const nameParts = alt.split(" ");
    const initials =
      nameParts
        .map((part) => part.charAt(0))
        .join("")
        .toUpperCase() || "G";
    return initials;
  };

  const tailwindBuildColors = [
    "bg-category-surface-rose",
    "bg-category-surface-green", 
    "bg-category-surface-teal",
    "bg-category-surface-blue",
    "bg-category-surface-purple",
    "bg-category-surface-red",
    "bg-category-surface-orange",
    "bg-category-surface-brown",

    "text-category-surface-rose",
    "text-category-surface-green",
    "text-category-surface-teal", 
    "text-category-surface-blue",
    "text-category-surface-purple",
    "text-category-surface-red",
    "text-category-surface-orange",
    "text-category-surface-brown",

    "bg-tag-text-green",
    "bg-tag-text-teal", 
    "bg-tag-text-blue",
    "bg-tag-text-purple",
    "bg-tag-text-rose",
    "bg-tag-text-red",
    "bg-tag-text-orange",
    "bg-tag-text-brown",

    "text-tag-text-green",
    "text-tag-text-teal", 
    "text-tag-text-blue",
    "text-tag-text-purple",
    "text-tag-text-rose",
    "text-tag-text-red",
    "text-tag-text-orange",
    "text-tag-text-brown",

  ];

  return (
    <RadixAvatar.Root
      {...props}
      data-cy={dataCy}
      onClick={onClick}
      className="group cursor-pointer"
      id={id}
    >
      <>
        <RadixAvatar.Image
          className={img()}
          src={src}
          alt={`Profile-Image: ${alt}`}
          style={{ overflow: "hidden" }}
        />
        <RadixAvatar.Fallback
          className={cn(fallback(), getInitialsColor(alt, "tailwind"))}
        >
          {notLoggedIn ? (
            <div className="flex justify-center">
              <Icon name="user-fill" />
            </div>
          ) : (
            getInitials(alt)
          )}
        </RadixAvatar.Fallback>
      </>
      {elements}
    </RadixAvatar.Root>
  );
};
Avatar.displayName = "Avatar";
export default Avatar;
